import { useCurrentRapidData, useMediaStore } from "@/store";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";
import { fileFromUrl } from "@utils/fileFromUrl";
import { useCallback, useRef } from "react";
import { MediaItemFile } from "@/types/media";
import { useMediaUtils } from "./useMediaUtils";

export const useAddExampleData = (opts: { stable: boolean } = { stable: true }) => {
    const rapidData = useCurrentRapidData();
    const { testWithMoreFiles } = useTestWithMoreFiles();
    const media = useMediaStore();
    const { setCurrentMedia } = useMediaUtils();
    const stableCalledRef = useRef(false);

    const addExampleDataFiles = useCallback(async () => {
        if (!rapidData?.exampleData?.[0]?.authenticatedData) return;

        if (opts.stable) {
            if (stableCalledRef.current) return;
            stableCalledRef.current = true;
        }

        let createdFiles: MediaItemFile[] = [];
        for (const exampleData of rapidData?.exampleData || []) {
            if (exampleData?.authenticatedData) {
                try {
                    const authData = exampleData.authenticatedData;
                    const fileName = exampleData.filePath.split("/")[1];
                    const storageUploadDirOverride = exampleData.filePath.split("/")[0];
                    const file: MediaItemFile = await fileFromUrl({
                        url: authData.url,
                        name: fileName
                    });
                    file.storageUploadDirOverride = storageUploadDirOverride;
                    file.id = fileName;
                    file.uploadResult = {
                        downloadUrl: authData.url,
                        filename: exampleData.filePath
                    };
                    if (exampleData.resizedFilePath && authData.resizedUrl) {
                        file.uploadResizedResult = {
                            downloadUrl: authData.resizedUrl,
                            filename: exampleData.resizedFilePath
                        };
                    }
                    file.skipUpload = true;

                    createdFiles.push(file);
                } catch (error) {
                    console.error("Error adding creation data files:", error);
                }
            }
        }
        const createdMediaItems = await testWithMoreFiles(createdFiles);
        const firstMediaItem = createdMediaItems?.[0];

        if (firstMediaItem) {
            const frame = firstMediaItem.isVideo
                ? media[firstMediaItem.segmentToFrameMediaItemIdMap?.[0]!]
                : firstMediaItem;
            setCurrentMedia({ file: firstMediaItem, frameSegment: 0, frame });
        }
    }, [rapidData, testWithMoreFiles, setCurrentMedia, media, opts.stable]);

    return { addExampleDataFiles };
};
